import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Plus, Check, Package, Search, ChevronDown, Upload, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { itemsAPI, customFieldsAPI } from '../../services/part2-api';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from './ConfirmModal';

// Common units list for searchable dropdown
const UNIT_OPTIONS = [
  { value: 'PCS', label: 'Pieces (PCS)', category: 'Count' },
  { value: 'NOS', label: 'Numbers (NOS)', category: 'Count' },
  { value: 'PAIR', label: 'Pair', category: 'Count' },
  { value: 'SET', label: 'Set', category: 'Count' },
  { value: 'DOZEN', label: 'Dozen (DZN)', category: 'Count' },
  { value: 'BOX', label: 'Box', category: 'Packaging' },
  { value: 'PACK', label: 'Pack', category: 'Packaging' },
  { value: 'PKT', label: 'Packet', category: 'Packaging' },
  { value: 'CARTON', label: 'Carton', category: 'Packaging' },
  { value: 'CASE', label: 'Case', category: 'Packaging' },
  { value: 'BUNDLE', label: 'Bundle', category: 'Packaging' },
  { value: 'KG', label: 'Kilogram (KG)', category: 'Weight' },
  { value: 'GM', label: 'Gram (GM)', category: 'Weight' },
  { value: 'MG', label: 'Milligram (MG)', category: 'Weight' },
  { value: 'QTL', label: 'Quintal', category: 'Weight' },
  { value: 'TON', label: 'Metric Ton', category: 'Weight' },
  { value: 'LTR', label: 'Liter (L)', category: 'Volume' },
  { value: 'ML', label: 'Milliliter (ML)', category: 'Volume' },
  { value: 'GAL', label: 'Gallon', category: 'Volume' },
  { value: 'MTR', label: 'Meter (M)', category: 'Length' },
  { value: 'CM', label: 'Centimeter (CM)', category: 'Length' },
  { value: 'MM', label: 'Millimeter (MM)', category: 'Length' },
  { value: 'FT', label: 'Feet (FT)', category: 'Length' },
  { value: 'IN', label: 'Inch (IN)', category: 'Length' },
  { value: 'YD', label: 'Yard', category: 'Length' },
  { value: 'SQM', label: 'Square Meter (SQM)', category: 'Area' },
  { value: 'SQFT', label: 'Square Feet (SQFT)', category: 'Area' },
  { value: 'BTL', label: 'Bottle', category: 'Other' },
  { value: 'CAN', label: 'Can', category: 'Other' },
  { value: 'JAR', label: 'Jar', category: 'Other' },
  { value: 'TUBE', label: 'Tube', category: 'Other' },
  { value: 'ROLL', label: 'Roll', category: 'Other' },
  { value: 'SHEET', label: 'Sheet', category: 'Other' },
  { value: 'BAG', label: 'Bag', category: 'Other' },
  { value: 'SACK', label: 'Sack', category: 'Other' },
  { value: 'DRUM', label: 'Drum', category: 'Other' },
  { value: 'HOUR', label: 'Hour (HR)', category: 'Time' },
  { value: 'DAY', label: 'Day', category: 'Time' },
  { value: 'WEEK', label: 'Week', category: 'Time' },
  { value: 'MONTH', label: 'Month', category: 'Time' },
];

// Searchable Unit Dropdown Component
const SearchableUnitSelect = ({ value, onChange, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const filteredOptions = UNIT_OPTIONS.filter(opt => 
    opt.label.toLowerCase().includes(search.toLowerCase()) ||
    opt.value.toLowerCase().includes(search.toLowerCase())
  );

  // Group by category
  const groupedOptions = filteredOptions.reduce((acc, opt) => {
    if (!acc[opt.category]) acc[opt.category] = [];
    acc[opt.category].push(opt);
    return acc;
  }, {});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedLabel = UNIT_OPTIONS.find(o => o.value === value)?.label || value || 'Select unit';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => { setIsOpen(!isOpen); setTimeout(() => inputRef.current?.focus(), 100); }}
        className={`w-full px-3 py-2 text-left text-sm bg-white border rounded-lg flex items-center justify-between ${
          error ? 'border-red-500' : 'border-slate-200 hover:border-slate-300'
        }`}
        data-testid="unit-select-trigger"
      >
        <span className={value ? 'text-slate-700' : 'text-slate-400'}>{selectedLabel}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search units..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                data-testid="unit-search-input"
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48">
            {Object.keys(groupedOptions).length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-slate-500">No units found</div>
            ) : (
              Object.entries(groupedOptions).map(([category, options]) => (
                <div key={category}>
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 sticky top-0">
                    {category}
                  </div>
                  {options.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { onChange(opt.value); setIsOpen(false); setSearch(''); }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-teal-50 flex items-center justify-between ${
                        value === opt.value ? 'bg-teal-50 text-teal-700' : 'text-slate-700'
                      }`}
                    >
                      <span>{opt.label}</span>
                      {value === opt.value && <Check className="w-4 h-4 text-teal-600" />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Step definitions with left-side navigation
const STEPS = [
  { id: 'basic', label: 'Basic Info', icon: '1' },
  { id: 'details', label: 'Details & SKU', icon: '2' },
  { id: 'units', label: 'Units & Stock', icon: '3' },
  { id: 'pricing', label: 'Pricing & Tax', icon: '4' },
  { id: 'media', label: 'Images', icon: '5' },
];

const AddItemModal = ({ item, onClose, onSave }) => {
  const [currentStep, setCurrentStep] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);

  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    item_type: 'product',
    category: '',
    description: '',
    status: 'active',
    show_in_online_store: false,
    
    // Details & SKU
    code: '',  // SKU / Item Code
    barcode: '',
    hsn: '',  // HSN/SAC Code
    brand: '',
    manufacturer: '',
    
    // Units & Stock
    primary_unit: 'PCS',
    alternate_unit_name: '',
    conversion_factor: '',
    opening_stock: 0,
    current_stock: 0,
    stock_as_of_date: new Date().toISOString().split('T')[0],
    reorder_level: '',
    enable_low_stock_warning: false,
    low_stock_threshold: '',
    expiry_date: '',
    
    // Pricing & Tax
    sales_price: 0,
    mrp: '',
    price_type: 'exclusive',
    discount_percent: 0,
    tax_rate: 18,
    purchase_price: 0,
    
    // Media
    images: [],
    primary_image_url: '',
  });

  // Fetch custom field definitions
  useEffect(() => {
    const fetchCustomFieldDefinitions = async () => {
      try {
        const response = await customFieldsAPI.list({ status: 'active' });
        setCustomFieldDefinitions(response.data || []);
      } catch (error) {
        console.error('Error fetching custom field definitions:', error);
      }
    };
    fetchCustomFieldDefinitions();
  }, []);

  useEffect(() => {
    if (item) {
      // Map existing item to form data
      setFormData(prev => ({
        ...prev,
        name: item.name || '',
        item_type: item.item_type || 'product',
        category: item.category || '',
        description: item.description || '',
        status: item.status || 'active',
        show_in_online_store: item.show_in_online_store || false,
        code: item.code || item.sku || '',
        barcode: item.barcode || '',
        hsn: item.hsn || item.hsn_code || '',
        brand: item.brand || '',
        manufacturer: item.manufacturer || '',
        primary_unit: item.primary_unit || item.unit || 'PCS',
        opening_stock: item.stock_config?.opening_stock ?? 0,
        current_stock: item.current_stock ?? 0,
        reorder_level: item.reorder_level || item.reorder_point || '',
        enable_low_stock_warning: item.stock_config?.enable_low_stock_warning || false,
        low_stock_threshold: item.stock_config?.low_stock_threshold || item.low_stock_alert || '',
        expiry_date: item.expiry_date || '',
        sales_price: item.pricing?.sales_price || item.selling_price || 0,
        mrp: item.mrp || '',
        price_type: item.pricing?.price_type || 'exclusive',
        discount_percent: item.pricing?.discount_percent || 0,
        tax_rate: item.tax_rate || 18,
        purchase_price: item.pricing?.purchase_price || item.purchase_price || 0,
        images: item.images || [],
        primary_image_url: item.primary_image_url || item.image || '',
      }));
    }
  }, [item]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsFormDirty(true);
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleClose = () => {
    if (isFormDirty && !item) {
      setConfirmModal({
        title: 'Discard Changes',
        description: 'Your entered details will be lost.',
        confirmLabel: 'Discard',
        confirmClass: 'bg-red-600 hover:bg-red-700',
        onConfirm: () => { setConfirmModal(null); onClose(); },
      });
    } else {
      onClose();
    }
  };

  const validateStep = (stepId) => {
    const errors = {};
    
    if (stepId === 'basic') {
      if (!formData.name.trim()) {
        errors.name = 'Item name is required';
      } else if (formData.name.trim().length < 2) {
        errors.name = 'Item name must be at least 2 characters';
      }
    }
    
    if (stepId === 'units') {
      if (!formData.primary_unit) {
        errors.primary_unit = 'Unit is required';
      }
    }
    
    if (stepId === 'pricing') {
      if (formData.sales_price < 0) {
        errors.sales_price = 'Sales price cannot be negative';
      }
      if (formData.purchase_price < 0) {
        errors.purchase_price = 'Purchase price cannot be negative';
      }
    }
    
    return errors;
  };

  const canProceedToNextStep = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please fix validation errors');
      return false;
    }
    setFieldErrors({});
    setError('');
    return true;
  };

  const handleNextStep = () => {
    if (!canProceedToNextStep()) return;
    const currentIndex = STEPS.findIndex(s => s.id === currentStep);
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1].id);
    }
  };

  const handlePreviousStep = () => {
    const currentIndex = STEPS.findIndex(s => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1].id);
    }
  };

  const handleSubmit = async () => {
    // Validate all steps
    let allErrors = {};
    STEPS.forEach(step => {
      const stepErrors = validateStep(step.id);
      allErrors = { ...allErrors, ...stepErrors };
    });
    
    if (Object.keys(allErrors).length > 0) {
      setFieldErrors(allErrors);
      setError(`Please fix ${Object.keys(allErrors).length} validation error(s)`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Prepare API data
      const apiData = {
        name: formData.name,
        code: formData.code || null,
        item_type: formData.item_type,
        category: formData.category || null,
        brand: formData.brand || null,
        description: formData.description || null,
        status: formData.status,
        show_in_online_store: formData.show_in_online_store,
        barcode: formData.barcode || null,
        hsn: formData.hsn || null,
        manufacturer: formData.manufacturer || null,
        
        primary_unit: formData.primary_unit,
        
        pricing: {
          sales_price: parseFloat(formData.sales_price) || 0,
          price_type: formData.price_type,
          discount_percent: parseFloat(formData.discount_percent) || 0,
          purchase_price: parseFloat(formData.purchase_price) || 0,
        },
        
        // Direct fields for Postgres compatibility
        selling_price: parseFloat(formData.sales_price) || 0,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        mrp: parseFloat(formData.mrp) || null,
        tax_rate: parseFloat(formData.tax_rate) || 18,
        
        stock_config: formData.item_type === 'product' ? {
          opening_stock: parseFloat(formData.opening_stock) || 0,
          stock_as_of_date: formData.stock_as_of_date || null,
          enable_low_stock_warning: formData.enable_low_stock_warning,
          low_stock_threshold: formData.low_stock_threshold ? parseFloat(formData.low_stock_threshold) : null,
        } : null,
        // Send current_stock only on edit so backend can restock item_stocks ledger
        ...(item ? { current_stock: parseFloat(formData.current_stock) || 0 } : {}),
        
        reorder_level: formData.reorder_level ? parseFloat(formData.reorder_level) : null,
        expiry_date: formData.expiry_date || null,
        
        images: [],
        primary_image_url: null,
      };

      let response;
      if (item) {
        response = await itemsAPI.update(item.id, apiData);
        toast.success('Product updated successfully');
      } else {
        response = await itemsAPI.create(apiData);
        toast.success('Product created successfully');
      }

      // Upload images to S3 (only new files, not existing server images)
      const savedItemId = item?.id || response?.data?.id;
      const imageFiles = (formData.images || []).filter(img => img.file);
      if (imageFiles.length > 0 && savedItemId) {
        for (let i = 0; i < imageFiles.length; i++) {
          try {
            const isPrimary = imageFiles[i].url === formData.primary_image_url;
            await itemsAPI.uploadImage(savedItemId, imageFiles[i].file, isPrimary || i === 0);
          } catch (uploadErr) {
            console.error(`Failed to upload image ${i + 1}:`, uploadErr);
            toast.error(`Image "${imageFiles[i].filename}" failed to upload`);
          }
        }
      }

      onSave();
    } catch (err) {
      console.error('Item save error:', err);
      const errorMsg = getApiErrorMessage(err, 'Failed to save product');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    if (formData.images.length >= 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const newImage = { url: previewUrl, filename: file.name, file };
    const newImages = [...formData.images, newImage];
    handleInputChange('images', newImages);

    // Set as primary if first image
    if (newImages.length === 1) {
      handleInputChange('primary_image_url', previewUrl);
    }
  };

  const handleRemoveImage = (imageUrl) => {
    // Revoke blob URL to free memory
    if (imageUrl && imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl);
    }
    const newImages = formData.images.filter(img => img.url !== imageUrl);
    handleInputChange('images', newImages);

    // Update primary if removed
    if (formData.primary_image_url === imageUrl) {
      handleInputChange('primary_image_url', newImages.length > 0 ? newImages[0].url : '');
    }
  };

  const handleSetPrimary = (imageUrl) => {
    handleInputChange('primary_image_url', imageUrl);
  };

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);

  return (
    <>
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white shadow-2xl flex flex-col md:flex-row overflow-hidden w-full h-full sm:rounded-xl sm:max-w-[900px] sm:w-[96vw] sm:h-[80vh] sm:max-h-[700px]" data-testid="add-item-modal">
        
        {/* Left Sidebar - Step Navigation */}
        <div className="hidden md:flex w-56 bg-gradient-to-b from-slate-50 to-slate-100 border-r flex-col flex-shrink-0">
          <div className="p-5 border-b bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-800">{item ? 'Edit Product' : 'New Product'}</h2>
                <p className="text-[10px] text-slate-400 mt-0.5">Step {currentStepIndex + 1} of {STEPS.length}</p>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-3">
            {STEPS.map((step, index) => {
              const isActive = currentStep === step.id;
              const isCompleted = index < currentStepIndex;
              
              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(step.id)}
                  className={`w-full text-left p-3 rounded-lg mb-1.5 transition-all flex items-center gap-3 ${
                    isActive
                      ? 'bg-teal-600 text-white shadow-sm'
                      : isCompleted
                      ? 'bg-white text-teal-700 hover:bg-teal-50 border border-teal-200'
                      : 'bg-white text-slate-600 hover:bg-slate-50 border border-transparent'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                    isActive ? 'bg-white/20 text-white' : 
                    isCompleted ? 'bg-teal-100 text-teal-700' : 
                    'bg-slate-100 text-slate-500'
                  }`}>
                    {isCompleted ? <Check className="w-3.5 h-3.5" /> : step.icon}
                  </div>
                  <span className="text-xs font-medium">{step.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-white flex-shrink-0">
            <div className="md:hidden">
              <h3 className="text-sm font-semibold text-slate-800">{STEPS.find(s => s.id === currentStep)?.label}</h3>
              <p className="text-[10px] text-slate-400">Step {currentStepIndex + 1} of {STEPS.length}</p>
            </div>
            <div className="hidden md:block">
              <h3 className="text-base font-semibold text-slate-800">{STEPS.find(s => s.id === currentStep)?.label}</h3>
            </div>
            <button onClick={handleClose} className="p-1.5 hover:bg-slate-100 rounded-lg" disabled={loading}>
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Mobile Step Indicator */}
          <div className="md:hidden flex items-center gap-1 px-4 py-2 bg-slate-50 border-b overflow-x-auto">
            {STEPS.map((step, index) => (
              <button
                key={step.id}
                onClick={() => setCurrentStep(step.id)}
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium ${
                  currentStep === step.id
                    ? 'bg-teal-600 text-white'
                    : index < currentStepIndex
                    ? 'bg-teal-100 text-teal-700'
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {step.label}
              </button>
            ))}
          </div>

          {/* Error Banner */}
          {error && (
            <div className="mx-4 mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs">
              {error}
            </div>
          )}

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Basic Info Step */}
            {currentStep === 'basic' && (
              <div className="space-y-4 max-w-lg">
                {/* Item Type */}
                <div>
                  <Label className="text-xs text-slate-600 mb-2 block">Item Type *</Label>
                  <div className="flex gap-2">
                    {['product', 'service'].map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleInputChange('item_type', type)}
                        className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          formData.item_type === type
                            ? 'bg-teal-600 text-white shadow-sm'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Item Name */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Item Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter product name"
                    className={fieldErrors.name ? 'border-red-500' : ''}
                    data-testid="item-name-input"
                  />
                  {fieldErrors.name && <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>}
                </div>

                {/* Category */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Category</Label>
                  <Input
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    placeholder="e.g., Electronics, Grocery, Pharma"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Brief product description"
                    rows={2}
                    className="resize-none"
                  />
                </div>

                {/* Status */}
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label className="text-xs text-slate-600 mb-1.5 block">Status</Label>
                    <Select value={formData.status} onValueChange={(v) => handleInputChange('status', v)}>
                      <SelectTrigger className="text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center pt-5">
                    <input
                      type="checkbox"
                      id="show_online"
                      checked={formData.show_in_online_store}
                      onChange={(e) => handleInputChange('show_in_online_store', e.target.checked)}
                      className="w-4 h-4 text-teal-600 border-slate-300 rounded"
                    />
                    <Label htmlFor="show_online" className="ml-2 text-xs cursor-pointer">Show Online</Label>
                  </div>
                </div>
              </div>
            )}

            {/* Details & SKU Step */}
            {currentStep === 'details' && (
              <div className="space-y-4 max-w-lg">
                <div className="grid grid-cols-2 gap-4">
                  {/* SKU / Item Code */}
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">SKU / Item Code</Label>
                    <Input
                      value={formData.code}
                      onChange={(e) => handleInputChange('code', e.target.value)}
                      placeholder="e.g., SKU-001"
                      data-testid="item-sku-input"
                    />
                  </div>

                  {/* Barcode */}
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">Barcode</Label>
                    <Input
                      value={formData.barcode}
                      onChange={(e) => handleInputChange('barcode', e.target.value)}
                      placeholder="Scan or enter barcode"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* HSN/SAC Code */}
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">HSN/SAC Code</Label>
                    <Input
                      value={formData.hsn}
                      onChange={(e) => handleInputChange('hsn', e.target.value)}
                      placeholder="e.g., 30049099"
                      data-testid="item-hsn-input"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Required for GST compliance</p>
                  </div>

                  {/* Brand */}
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">Brand</Label>
                    <Input
                      value={formData.brand}
                      onChange={(e) => handleInputChange('brand', e.target.value)}
                      placeholder="Brand name"
                      data-testid="item-brand-input"
                    />
                  </div>
                </div>

                {/* Manufacturer */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Manufacturer</Label>
                  <Input
                    value={formData.manufacturer}
                    onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                    placeholder="Manufacturer name (optional)"
                  />
                </div>
              </div>
            )}

            {/* Units & Stock Step */}
            {currentStep === 'units' && (
              <div className="space-y-4 max-w-lg">
                {/* Primary Unit - Searchable */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Primary Unit *</Label>
                  <SearchableUnitSelect
                    value={formData.primary_unit}
                    onChange={(v) => handleInputChange('primary_unit', v)}
                    error={fieldErrors.primary_unit}
                  />
                  {fieldErrors.primary_unit && <p className="mt-1 text-xs text-red-600">{fieldErrors.primary_unit}</p>}
                </div>

                {formData.item_type === 'product' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Opening Stock — read-only on edit */}
                      <div>
                        <Label className="text-xs text-slate-600 mb-1.5 block">
                          Opening Stock
                          {item && <span className="ml-1 text-slate-400">(set at creation)</span>}
                        </Label>
                        <Input
                          type="number"
                          value={formData.opening_stock}
                          onChange={(e) => handleInputChange('opening_stock', e.target.value)}
                          min="0"
                          step="0.01"
                          disabled={!!item}
                          className={item ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : ''}
                        />
                      </div>

                      {/* Reorder Level */}
                      <div>
                        <Label className="text-xs text-slate-600 mb-1.5 block">Reorder Level</Label>
                        <Input
                          type="number"
                          value={formData.reorder_level}
                          onChange={(e) => handleInputChange('reorder_level', e.target.value)}
                          placeholder="Alert when below"
                          min="0"
                        />
                      </div>
                    </div>

                    {/* Current Stock — only visible on edit, for restocking */}
                    {item && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <Label className="text-xs font-medium text-blue-700 mb-1.5 block">
                          Current Stock (Restock)
                        </Label>
                        <p className="text-xs text-blue-600 mb-2">
                          Edit this to correct or replenish the available quantity. POS and reports will use this value.
                        </p>
                        <Input
                          type="number"
                          value={formData.current_stock}
                          onChange={(e) => handleInputChange('current_stock', e.target.value)}
                          min="0"
                          step="0.01"
                          className="bg-white"
                        />
                      </div>
                    )}

                    {/* Expiry Date */}
                    <div>
                      <Label className="text-xs text-slate-600 mb-1.5 block">Expiry Date (if applicable)</Label>
                      <Input
                        type="date"
                        value={formData.expiry_date}
                        onChange={(e) => handleInputChange('expiry_date', e.target.value)}
                      />
                    </div>

                    {/* Low Stock Warning */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id="low_stock_warning"
                          checked={formData.enable_low_stock_warning}
                          onChange={(e) => handleInputChange('enable_low_stock_warning', e.target.checked)}
                          className="w-4 h-4 text-amber-600 border-amber-300 rounded"
                        />
                        <Label htmlFor="low_stock_warning" className="text-xs font-medium cursor-pointer">
                          Enable Low Stock Alert
                        </Label>
                      </div>
                      {formData.enable_low_stock_warning && (
                        <Input
                          type="number"
                          value={formData.low_stock_threshold}
                          onChange={(e) => handleInputChange('low_stock_threshold', e.target.value)}
                          placeholder="Alert threshold quantity"
                          className="mt-2"
                          min="0"
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Pricing & Tax Step */}
            {currentStep === 'pricing' && (
              <div className="space-y-4 max-w-lg">
                {/* Sales Pricing */}
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-teal-700 mb-3">Sales Pricing</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-600 mb-1.5 block">Selling Price *</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                        <Input
                          type="number"
                          value={formData.sales_price}
                          onChange={(e) => handleInputChange('sales_price', e.target.value)}
                          className={`pl-7 ${fieldErrors.sales_price ? 'border-red-500' : ''}`}
                          min="0"
                          step="0.01"
                          data-testid="item-price-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 mb-1.5 block">MRP</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                        <Input
                          type="number"
                          value={formData.mrp}
                          onChange={(e) => handleInputChange('mrp', e.target.value)}
                          className="pl-7"
                          min="0"
                          step="0.01"
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs text-slate-600 mb-1.5 block">Price Type</Label>
                    <Select value={formData.price_type} onValueChange={(v) => handleInputChange('price_type', v)}>
                      <SelectTrigger className="text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="exclusive">Exclusive of Tax</SelectItem>
                        <SelectItem value="inclusive">Inclusive of Tax</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Purchase Pricing */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-blue-700 mb-3">Purchase Pricing</h4>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">Purchase Price</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                      <Input
                        type="number"
                        value={formData.purchase_price}
                        onChange={(e) => handleInputChange('purchase_price', e.target.value)}
                        className={`pl-7 ${fieldErrors.purchase_price ? 'border-red-500' : ''}`}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                {/* Tax Rate */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">GST Tax Rate (%)</Label>
                  <Select value={String(formData.tax_rate)} onValueChange={(v) => handleInputChange('tax_rate', parseFloat(v))}>
                    <SelectTrigger className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">GST 0%</SelectItem>
                      <SelectItem value="5">GST 5%</SelectItem>
                      <SelectItem value="12">GST 12%</SelectItem>
                      <SelectItem value="18">GST 18%</SelectItem>
                      <SelectItem value="28">GST 28%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Margin Preview */}
                {formData.sales_price > 0 && formData.purchase_price > 0 && (
                  <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
                    <span className="text-xs text-slate-600">Profit Margin</span>
                    <span className={`text-sm font-semibold ${
                      ((formData.sales_price - formData.purchase_price) / formData.sales_price * 100) > 20
                        ? 'text-emerald-600'
                        : 'text-amber-600'
                    }`}>
                      {((formData.sales_price - formData.purchase_price) / formData.sales_price * 100).toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Images Step */}
            {currentStep === 'media' && (
              <div className="space-y-4">
                {/* Upload Area */}
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-teal-400 transition-colors">
                  <Upload className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Drag & drop images or click to browse</p>
                  <p className="text-xs text-slate-400 mb-3">Max 5 images, 5 MB each</p>
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium cursor-pointer">
                    <Plus className="w-4 h-4" />
                    Add Image
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                  </label>
                </div>

                {/* Image Grid */}
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {formData.images.map((img, index) => (
                      <div
                        key={index}
                        className={`relative group rounded-lg overflow-hidden aspect-square border-2 ${
                          img.url === formData.primary_image_url 
                            ? 'border-teal-500 ring-2 ring-teal-100' 
                            : 'border-slate-200'
                        }`}
                      >
                        <img
                          src={img.url}
                          alt={img.filename || `Image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {img.url === formData.primary_image_url && (
                          <div className="absolute top-1 left-1 bg-teal-600 text-white px-1.5 py-0.5 rounded text-[9px] font-medium">
                            Primary
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          {img.url !== formData.primary_image_url && (
                            <button
                              type="button"
                              onClick={() => handleSetPrimary(img.url)}
                              className="p-1.5 bg-white rounded text-teal-600 hover:bg-teal-50"
                              title="Set as primary"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(img.url)}
                            className="p-1.5 bg-white rounded text-red-600 hover:bg-red-50"
                            title="Remove"
                            data-testid={`remove-image-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t bg-slate-50 flex-shrink-0">
            <div>
              {currentStepIndex > 0 && (
                <Button variant="outline" size="sm" onClick={handlePreviousStep} disabled={loading}>
                  Previous
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              {currentStepIndex < STEPS.length - 1 ? (
                <Button size="sm" onClick={handleNextStep} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
                  Next
                </Button>
              ) : (
                <Button size="sm" onClick={handleSubmit} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
                  <Save className="w-4 h-4 mr-1.5" />
                  {loading ? 'Saving...' : item ? 'Update' : 'Create'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    <ConfirmModal
      isOpen={!!confirmModal}
      title={confirmModal?.title}
      description={confirmModal?.description}
      confirmLabel={confirmModal?.confirmLabel}
      confirmClass={confirmModal?.confirmClass}
      onConfirm={confirmModal?.onConfirm}
      onClose={() => setConfirmModal(null)}
    />
    </>
  );
};

export default AddItemModal;
